import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, Textarea } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AccessibleAccountsDocument,
    CloseAccountToPortalDocument,
    OpenAccountToPortalDocument,
} from '../../graphql/__generated__/types';
import { refusalOf } from './portalAccess';

export type PortalAccessDirection = 'open' | 'close';

interface Props {
    accountId: string;
    accountName: string;
    direction: PortalAccessDirection;
    onClose: () => void;
}

const PortalAccessDialog = ({ accountId, accountName, direction, onClose }: Props) => {
    const [reason, setReason] = useState('');
    const opening = direction === 'open';

    // Two types over the same row are normalised under two keys, so the mutation result
    // cannot update the list on its own; refetching un-greys the account in the onboarding tree.
    const options = { refetchQueries: [AccessibleAccountsDocument], awaitRefetchQueries: true };
    const [openAccount, openState] = useMutation(OpenAccountToPortalDocument, options);
    const [closeAccount, closeState] = useMutation(CloseAccountToPortalDocument, options);

    const { loading, error } = opening ? openState : closeState;
    const done = opening ? openState.data?.openAccountToPortal : closeState.data?.closeAccountToPortal;

    const submit = () => {
        const run = opening
            ? openAccount({ variables: { organizationId: accountId } })
            : closeAccount({ variables: { organizationId: accountId, reason: reason.trim() } });
        // Swallow the rejection: the refusal is read off error below, otherwise every refusal logs an unhandled rejection.
        run.catch(() => {});
    };

    const title = opening
        ? c('Portal access').t`Open this account to the portal`
        : c('Portal access').t`Close this account to the portal`;

    // A motive is required on screen although the API takes it as optional: it is the only trace of why.
    const blocked = !opening && reason.trim().length === 0;

    return (
        <Modal open onClose={onClose} title={title} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">{accountName}</p>

                    {done ? (
                        <Alert type="success">
                            {done.portalEnabled
                                ? c('Portal access')
                                      .t`The account is now open to the portal. Nobody has an access yet: onboard the people who should get one, each with their role and their perimeter.`
                                : c('Portal access')
                                      .t`The account is closed to the portal. No new invitation can be sent on it, and no access was taken away — there was none left to take.`}
                        </Alert>
                    ) : (
                        <>
                            <Alert type="warning">
                                {opening
                                    ? c('Portal access')
                                          .t`Opening grants an access to nobody. It makes the account eligible to receive portal users; who actually enters is decided afterwards, membership by membership, by an invitation carrying its own role and perimeter.`
                                    : c('Portal access')
                                          .t`Closing takes nobody's access away. It stops new invitations on this account; the people already attached keep signing in until their membership is revoked, one by one.`}
                            </Alert>

                            {!opening && (
                                <div>
                                    <label
                                        htmlFor="portal-access-reason"
                                        className="mb-1.5 block text-sm font-medium text-text-900"
                                    >
                                        {c('Portal access').t`Reason for closing`}
                                    </label>
                                    <Textarea
                                        id="portal-access-reason"
                                        value={reason}
                                        onValue={setReason}
                                        placeholder={c('Portal access')
                                            .t`Contract ended, account merged into another one…`}
                                    />
                                    <p className="mt-1.5 text-xs text-text-600">
                                        {c('Portal access')
                                            .t`It is journalled with the gesture, and it is the only trace of why the account was closed.`}
                                    </p>
                                </div>
                            )}

                            {error && <RefusalNotice refusal={refusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    // "Done" and not "Close": here "Close" is the name of the other gesture.
                    <Button category="default" onClick={onClose}>
                        {c('Portal access action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button
                            // destructive for the closing only: opening a client account is consequential, not destructive.
                            category={opening ? 'default' : 'destructive'}
                            loading={loading}
                            disabled={blocked}
                            onClick={submit}
                        >
                            {opening
                                ? c('Portal access action').t`Open the account`
                                : c('Portal access action').t`Close the account`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default PortalAccessDialog;
